/**
 * 旅行回忆系统 - 空间选择页面
 * 显示用户的所有空间，可以选择或创建空间
 * 系统管理员可以看到所有空间
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTravelMemories } from '../contexts/TravelMemoriesContext';
import { spaceApi } from '../services/travelMemoriesApi';
import './TravelMemoriesSpaceSelect.css';

const TravelMemoriesSpaceSelect = () => {
  const navigate = useNavigate();
  const { spaces, selectSpace, refreshSpaces, loading } = useTravelMemories();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // 检查是否是系统管理员
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setIsAdmin(user.isAdmin === true || user.isAdmin === 'true');
      } catch (e) {
        console.error('解析用户信息失败:', e);
      }
    }
    refreshSpaces();
  }, []);

  const handleSelectSpace = (space) => {
    // 如果系统管理员选择未加入的空间，需要先加入
    if (isAdmin && !space.role) {
      // TODO: 实现系统管理员加入空间的逻辑
      alert('系统管理员需要先加入空间才能访问');
      return;
    }
    selectSpace(space);
    navigate('/travel-memories');
  };

  const handleCreateSpace = () => {
    navigate('/travel-memories/spaces/new');
  };

  if (loading) {
    return (
      <div className="tm-space-select-loading">
        <div>加载中...</div>
      </div>
    );
  }

  // 分离已加入和未加入的空间（系统管理员视图）
  const joinedSpaces = spaces.filter(s => s.role);
  const unjoinedSpaces = isAdmin ? spaces.filter(s => !s.role) : [];

  return (
    <div className="tm-space-select">
      <div className="tm-space-select-container">
        <header className="tm-space-select-header">
          <h1>{isAdmin ? '所有空间' : '我的空间'}</h1>
          <p>{isAdmin ? '系统管理员可以查看和管理所有空间' : '选择一个空间开始记录旅行回忆'}</p>
        </header>

        {/* 已加入的空间 */}
        {joinedSpaces.length > 0 && (
          <div className="tm-spaces-section">
            <h2 className="tm-section-title">我的空间</h2>
            <div className="tm-spaces-grid">
              {joinedSpaces.map((space) => (
                <div
                  key={space._id}
                  className="tm-space-card"
                  onClick={() => handleSelectSpace(space)}
                >
                  {space.coverImage && (
                    <div className="tm-space-cover">
                      <img src={space.coverImage} alt={space.name} />
                    </div>
                  )}
                  <div className="tm-space-content">
                    <h3 className="tm-space-name">{space.name}</h3>
                    {space.description && (
                      <p className="tm-space-description">{space.description}</p>
                    )}
                    <div className="tm-space-meta">
                      <span className={`tm-space-role tm-role-${space.role}`}>
                        {space.role === 'owner' ? '所有者' : space.role === 'editor' ? '编辑者' : '查看者'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 未加入的空间（仅系统管理员可见） */}
        {isAdmin && unjoinedSpaces.length > 0 && (
          <div className="tm-spaces-section">
            <h2 className="tm-section-title">其他空间</h2>
            <div className="tm-spaces-grid">
              {unjoinedSpaces.map((space) => (
                <div
                  key={space._id}
                  className="tm-space-card tm-space-card-unjoined"
                  onClick={() => handleSelectSpace(space)}
                >
                  {space.coverImage && (
                    <div className="tm-space-cover">
                      <img src={space.coverImage} alt={space.name} />
                    </div>
                  )}
                  <div className="tm-space-content">
                    <h3 className="tm-space-name">{space.name}</h3>
                    {space.description && (
                      <p className="tm-space-description">{space.description}</p>
                    )}
                    <div className="tm-space-meta">
                      <span className="tm-space-role tm-role-unjoined">
                        未加入
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 空状态 */}
        {spaces.length === 0 && (
          <div className="tm-empty-state">
            <h2>还没有空间</h2>
            <p>创建一个空间开始记录你的旅行回忆吧</p>
          </div>
        )}

        {/* 创建新空间卡片 */}
        <div className="tm-spaces-section">
          <div className="tm-spaces-grid">
            <div
              className="tm-space-card tm-space-card-new"
              onClick={handleCreateSpace}
            >
              <div className="tm-space-card-new-content">
                <div className="tm-space-card-new-icon">+</div>
                <h3>创建新空间</h3>
                <p>开始记录新的旅行回忆</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelMemoriesSpaceSelect;
